import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { TransformScene, TAGS } from "@/components/three/TransformScene";
import { Eyebrow } from "@/components/site/SectionHead";
import { STAGES } from "@/content/site";

const StageText = ({ stage }) => (
  <div className="grid [grid-template-areas:'stack']">
    {STAGES.map((s, i) => (
      <div
        key={s.k}
        className={`[grid-area:stack] transition-[opacity,transform] duration-700 ease-[cubic-bezier(.22,1,.36,1)] ${i === stage ? "translate-y-0 opacity-100" : `pointer-events-none opacity-0 ${i < stage ? "-translate-y-6" : "translate-y-6"}`}`}
        aria-hidden={i !== stage}
        data-testid={`transform-stage-${i}`}
      >
        <h3 className="text-[1.9rem] font-[540] leading-[1.04] tracking-[-0.035em] text-fg sm:text-4xl lg:text-5xl">{s.title}</h3>
        <p className="mt-5 max-w-md text-[15px] leading-relaxed text-fg-2 sm:text-base">{s.text}</p>
      </div>
    ))}
  </div>
);

const Steps = ({ progress }) => (
  <div className="mt-8">
    <div className="relative h-px w-full max-w-md bg-white/15">
      <motion.div className="absolute inset-y-0 left-0 w-full origin-left bg-signal" style={{ scaleX: progress }} />
    </div>

  </div>
);

export const Transformation = () => {
  const ref = useRef(null);
  const tagRefs = useRef([]);
  const [stage, setStage] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => setStage(Math.min(3, Math.floor(v * 4))));

  return (
    <section ref={ref} id="how" className="relative h-[300svh]" data-testid="transformation-section" data-analytics-section="how">
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <TransformScene progress={scrollYProgress} tagRefs={tagRefs} className="absolute inset-0" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent md:bg-gradient-to-r md:from-ink/90 md:via-ink/30 md:to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-ink to-transparent" />
        <div className="pointer-events-none absolute inset-0 hidden md:block">
          {TAGS.map((tag, i) => (
            <div key={tag.id} ref={(n) => { tagRefs.current[i] = n; }} className="absolute left-0 top-0 opacity-0 transition-opacity duration-500" data-testid={`transform-tag-${tag.id}`}>
              <div className="-translate-x-1/2 -translate-y-full pb-3">
                <div className="flex flex-col items-center">
                  <span className="whitespace-nowrap rounded-full border border-white/15 bg-ink/70 px-3 py-1 mono text-[11px] uppercase tracking-[0.14em] text-fg backdrop-blur-md">{tag.label}</span>
                  <span className="h-6 w-px bg-gradient-to-b from-fg/60 to-transparent" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="shell relative z-10 flex h-full flex-col justify-end pb-10 md:justify-center md:pb-0">
          <div className="max-w-lg">
            <Eyebrow className="mb-8">How it works</Eyebrow>
            <StageText stage={stage} />
            <Steps progress={scrollYProgress} />
          </div>
        </div>
      </div>
    </section>
  );
};
